import { RichText } from '@graphcommerce/graphcms-ui'
import { LayoutHeader, LayoutTitle } from '@graphcommerce/next-ui'
import { useForm } from '@graphcommerce/react-hook-form'
import { Trans } from '@lingui/react'
import { Box, Button } from '@mui/material'
import { useRouter } from 'next/router'
import { RowConfiguratorFragment } from '../../graphql/configurator/RowConfigurator.gql'
import { ConfiguratorSection } from './ConfiguratorSection'
import { calculateTotalScore, findAgeQuestion } from './common'
import { ConfiguratorForm } from './types'

export function RowConfigurator(props: RowConfiguratorFragment) {
  const { title, description, results, sections, button } = props
  const router = useRouter()
  const finalResults = results.sort((a, b) => a.score - b.score)

  const form = useForm<ConfiguratorForm>({
    defaultValues: {
      sections: sections.map((section) => ({
        questions: section.questions.map(() => 1),
      })),
      age: 1,
    },
  })

  const { control, handleSubmit } = form

  const submit = handleSubmit((data) => {
    const age = findAgeQuestion(sections)?.answers?.[Number(data?.age) - 1]?.score ?? 0
    const totalScore = calculateTotalScore(data.sections, sections)

    const matchingResultsOnScore = finalResults.filter((result, index) => {
      const nextResult = finalResults.find((r, i) => i > index && r.score > result.score)
      if (!nextResult || result.score === nextResult.score) return totalScore >= result.score
      return totalScore >= result.score && totalScore < nextResult.score
    })

    const matchingResultsOnAge = matchingResultsOnScore.filter((result) => age > (result?.age ?? 0))

    let result: (typeof finalResults)[number] = finalResults?.[0]
    if (matchingResultsOnScore.length === 0) {
      // case 1: geen score match
    } else if (matchingResultsOnScore.length > 0 && matchingResultsOnAge.length === 0) {
      // case 2: wel score, geen age match
      result = matchingResultsOnScore?.[0]
    } else if (matchingResultsOnAge.length > 0) {
      // case 3: meerdere matches, neem die met kleinste positieve leeftijdsverschil
      result = matchingResultsOnAge.reduce((prev, current) => {
        const currentAgeDifference = age - Number(current?.age)
        const prevAgeDifference = age - Number(prev?.age)
        return currentAgeDifference < prevAgeDifference ? current : prev
      })
    }

    router.push(`/${result.resultUrl}`).catch(console.error)
  })

  return (
    <>
      <LayoutHeader floatingMd hideBackButton>
        <LayoutTitle size='small' component='span'>
          {title}
        </LayoutTitle>
      </LayoutHeader>

      <Box sx={(theme) => ({ mb: theme.spacings.xl })}>
        <LayoutTitle
          variant='h2'
          component='h2'
          gutterBottom={false}
          gutterTop={false}
          sx={(theme) => ({
            mb: theme.spacings.sm,
            '& h2': { textAlign: 'left', width: '100%', fontSize: { md: '40px' } },
          })}
        >
          {title}
        </LayoutTitle>

        <RichText raw={description?.raw} />

        <form onSubmit={submit}>
          {sections.map((section, index) => (
            <ConfiguratorSection
              configuratorSection={section}
              key={section.id}
              sectionIndex={index}
              control={control}
              sx={(theme) => ({ mt: theme.spacings.md })}
            />
          ))}

          <Button
            type='submit'
            variant='pill'
            color='primary'
            sx={(theme) => ({ mt: theme.spacings.lg })}
          >
            {button ?? <Trans id='Next' />}
          </Button>
        </form>
      </Box>
    </>
  )
}
